<template>
  <v-card color="transparent" class="rounded-custom" style="overflow: hidden;">
    <v-list-item class="primary" dark>
      <v-list-item-content>
        <v-list-item-title class="body-1 font-weight-bold font-italic">{{
          organism.scientificName
        }}</v-list-item-title>
        <v-list-item-subtitle class="body-2 font-weight-bold">{{
          organism.vernacularName
        }}</v-list-item-subtitle>
      </v-list-item-content>
      <v-list-item-action>
        <v-btn icon v-if="showCloseButton" @click="() => this.$emit('onClose')"
          ><v-icon>mdi-close</v-icon></v-btn
        >
      </v-list-item-action>
    </v-list-item>
    <OrganismGallery
      :organism-id="organism.id"
      v-if="organism.id"
    ></OrganismGallery>
    <v-expansion-panels accordion focusable>
      <v-expansion-panel>
        <v-expansion-panel-header class="font-weight-bold pt-0 pb-0">
          <template v-slot:default="{ open }">
            <SvgIcon
              icon="cp-library-info"
              :fill="open ? 'white' : '#0087af'"
              height="30px"
            ></SvgIcon>
            <span class="pl-4">Taxonomía</span>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="body-2 secondary--text">
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Reino:</span>
            </v-col>
            <v-col class="font-weight-regular">{{ organism.kingdom }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Phylum:</span>
            </v-col>
            <v-col class="font-weight-regular">{{ organism.phylum }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Clase:</span>
            </v-col>
            <v-col class="font-weight-regular">{{ organism.className }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Orden:</span>
            </v-col>
            <v-col class="font-weight-regular">{{ organism.order }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Familia:</span>
            </v-col>
            <v-col class="font-weight-regular">{{ organism.family }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Especie:</span>
            </v-col>
            <v-col class="font-weight-regular">{{ organism.genus }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Nombre científico:</span>
            </v-col>
            <v-col class="font-weight-regular font-italic">{{
              organism.scientificName
            }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Nombre común:</span>
            </v-col>
            <v-col class="font-weight-regular">{{
              organism.vernacularName
            }}</v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header class="font-weight-bold pt-0 pb-0">
          <template v-slot:default="{ open }">
            <SvgIcon
              icon="cp-library-metrics"
              :fill="open ? 'white' : '#0087af'"
              height="30px"
            ></SvgIcon>
            <span class="pl-4">Métrica general</span>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="body-2 secondary--text">
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">LT máx. (cm)</span>
            </v-col>
            <v-col class="font-weight-regular">{{
              organism.LT_max || "No informado"
            }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">LT medio (cm)</span>
            </v-col>
            <v-col class="font-weight-regular">{{
              organism.LT_mid || "No informado"
            }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium secondary--text"
                >Distribución batimétrica (m)</span
              >
            </v-col>
            <v-col class="font-weight-regular">{{
              organism.depth_range || "No informado"
            }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Talla de madurez (TPMS)</span>
            </v-col>
            <v-col
              v-html="organism.TPMS_field || 'No informado'"
              class="ql-editor"
            ></v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header class="font-weight-bold pt-0 pb-0">
          <template v-slot:default="{ open }">
            <SvgIcon
              icon="cp-library-fisheries-1"
              :fill="open ? 'white' : '#0087af'"
              height="30px"
            ></SvgIcon>
            <span class="pl-4">Recomendaciones para su captura</span>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="body-2 secondary--text">
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">N° máximo de ejemplares</span>
            </v-col>
            <v-col class="font-weight-regular">{{
              organism.N_max || "No informado"
            }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium"
                >Peso mínimo por ejemplar (Kg)</span
              >
            </v-col>
            <v-col class="font-weight-regular">{{
              organism.W_min || "No informado"
            }}</v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Veda reproductiva</span>
            </v-col>
            <v-col class="font-weight-regular">{{
              organism.veda || "No informado"
            }}</v-col>
          </v-row>
          <blockquote class="font-weight-light font-italic">
            *** "No constituyen normativa pesquera, sino recomendaciones
            realizadas por expertos"
          </blockquote>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header class="font-weight-bold pt-0 pb-0">
          <template v-slot:default="{ open }">
            <SvgIcon
              icon="cp-fish-1"
              :fill="open ? 'white' : '#0087af'"
              height="30px"
            ></SvgIcon>
            <span class="pl-4">Biología y Ecología</span>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="body-2 secondary--text">
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Distribución en Chile</span>
            </v-col>
            <v-col
              v-html="organism.locality_CL || 'No informado'"
              class="ql-editor"
            ></v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Hábitat</span>
            </v-col>
            <v-col
              v-html="organism.habitat || 'No informado'"
              class="ql-editor"
            ></v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Biología y ecología</span>
            </v-col>
            <v-col
              v-html="organism.biology || 'No informado'"
              class="ql-editor"
            ></v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel>
        <v-expansion-panel-header class="font-weight-bold pt-0 pb-0">
          <template v-slot:default="{ open }">
            <SvgIcon
              icon="cp-library-fisheries-2"
              :fill="open ? 'white' : '#0087af'"
              height="30px"
            ></SvgIcon>
            <span class="pl-4">Pesquerías y estado de conservación</span>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="body-2 secondary--text">
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Pesquería</span>
            </v-col>
            <v-col
              v-html="organism.fisheries || 'No informado'"
              class="ql-editor"
            ></v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Estado de conservación</span>
            </v-col>
            <v-col
              v-html="organism.conservation || 'No informado'"
              class="ql-editor"
            ></v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>

      <v-expansion-panel class="border-bottom-custom">
        <v-expansion-panel-header class="font-weight-bold pt-0 pb-0">
          <template v-slot:default="{ open }">
            <SvgIcon
              icon="cp-library-reference"
              :fill="open ? 'white' : '#0087af'"
              height="30px"
            ></SvgIcon>
            <span class="pl-4">Referencias</span>
          </template>
        </v-expansion-panel-header>
        <v-expansion-panel-content class="body-2 secondary--text">
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Referencias</span>
            </v-col>
            <v-col
              v-html="organism.references || 'No informado'"
              class="ql-editor"
            ></v-col>
          </v-row>
          <v-row>
            <v-col cols="4">
              <span class="font-weight-medium">Autor ficha</span>
            </v-col>
            <v-col
              v-html="organism.card_author || 'No informado'"
              class="ql-editor"
            ></v-col>
          </v-row>
        </v-expansion-panel-content>
      </v-expansion-panel>
    </v-expansion-panels>
  </v-card>
</template>

<script>
import OrganismGallery from "../Organism/OrganismGallery";
import SvgIcon from "@/components/SvgIcon";

export default {
  components: { OrganismGallery, SvgIcon },
  props: ["idOrganism", "showCloseButton"],
  data: () => ({
    organism: {},
  }),
  async mounted() {
    try {
      let { data: organismData } = await this.$http.get(
        "organism/" + this.idOrganism,
        {
          params: {
            where: {
              sp_card: true,
            },
          },
        }
      );
      this.organism = organismData;
      await this.$http.patch("organism/" + this.idOrganism, {
        mostSearched: organismData.mostSearched + 1,
      });
    } catch (err) {
      //console.log(err);
    }
  },
};
</script>

<style scoped>
.border-bottom-custom {
  border-radius: 0 0 20px 20px;
}

.v-expansion-panel-header {
  background-color: #c0e2e6 !important;
  color: var(--v-primary-base);
}

.v-expansion-panel-header--active {
  background-color: var(--v-primary-base) !important;
  color: white !important;
}

.v-expansion-panel-content {
  background-color: #c0e2e6 !important;
}

.ql-editor {
  min-height: 1em;
}
</style>
